import React, { useEffect, useMemo, useState } from 'react';
import { Grid3x3, Paintbrush, Eraser, Layers, Save } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';

/**
 * Compact 2D tilemap painter.
 * A tilemap is stored on `project.game_data.tilemaps[]` as:
 *   { id, name, rows, cols, cell_size, tile_url, palette: [{id,name,color,url}], cells: number[][] }
 * Each cell references a palette entry index (0 = empty).
 */
export default function TilemapPainter({ tilemap, onChange }) {
  const [cols, setCols] = useState(tilemap?.cols || 12);
  const [rows, setRows] = useState(tilemap?.rows || 8);
  const [brush, setBrush] = useState(1);
  const [erasing, setErasing] = useState(false);
  const [palette, setPalette] = useState(tilemap?.palette || [
    { id: 1, name: 'Ground', color: '#4B5563' },
    { id: 2, name: 'Grass',  color: '#22C55E' },
    { id: 3, name: 'Water',  color: '#38BDF8' },
    { id: 4, name: 'Sand',   color: '#EAB308' },
    { id: 5, name: 'Danger', color: '#EF4444' },
  ]);
  const [cells, setCells] = useState(() => tilemap?.cells || Array.from({ length: rows }, () => Array(cols).fill(0)));

  // Resize grid whenever rows/cols change.
  useEffect(() => {
    setCells((prev) => {
      const next = Array.from({ length: rows }, (_, r) =>
        Array.from({ length: cols }, (_, c) => (prev[r] && prev[r][c]) || 0)
      );
      return next;
    });
  }, [rows, cols]);

  const paintedCount = useMemo(() => cells.flat().filter(x => x > 0).length, [cells]);

  const paint = (r, c) => {
    setCells(prev => {
      const next = prev.map(row => row.slice());
      next[r][c] = erasing ? 0 : brush;
      return next;
    });
  };

  const clear = () => {
    if (!window.confirm('Clear the whole tilemap?')) return;
    setCells(Array.from({ length: rows }, () => Array(cols).fill(0)));
  };

  const save = () => {
    if (!onChange) return;
    onChange({
      id: tilemap?.id || `tilemap_${Date.now().toString(36)}`,
      name: tilemap?.name || 'Level 1',
      rows, cols,
      cell_size: tilemap?.cell_size || 1,
      palette,
      cells,
    });
  };

  const addPaletteColor = () => {
    const nextId = (palette[palette.length - 1]?.id || 0) + 1;
    setPalette(p => [...p, { id: nextId, name: `Tile ${nextId}`, color: '#8B5CF6' }]);
  };

  return (
    <div className="p-2 space-y-2" data-testid="tilemap-painter">
      <div className="flex items-center gap-2 flex-wrap">
        <Grid3x3 className="w-3 h-3 text-[#06B6D4]" />
        <span className="text-[11px] font-semibold text-white">Tilemap Painter</span>
        <span className="ml-auto text-[9px] font-mono text-zinc-500">{paintedCount} tiles · {cols}×{rows}</span>
      </div>

      {/* Dimension inputs */}
      <div className="grid grid-cols-2 gap-1">
        <div>
          <label className="text-[9px] uppercase text-zinc-500 block">Cols</label>
          <Input type="number" min="2" max="64"
            value={cols}
            onChange={(e) => setCols(Math.max(2, Math.min(64, parseInt(e.target.value) || 12)))}
            className="h-6 bg-transparent border-[#1E1E24] text-white text-[10px] font-mono"
            data-testid="tilemap-cols"
          />
        </div>
        <div>
          <label className="text-[9px] uppercase text-zinc-500 block">Rows</label>
          <Input type="number" min="2" max="64"
            value={rows}
            onChange={(e) => setRows(Math.max(2, Math.min(64, parseInt(e.target.value) || 8)))}
            className="h-6 bg-transparent border-[#1E1E24] text-white text-[10px] font-mono"
            data-testid="tilemap-rows"
          />
        </div>
      </div>

      {/* Palette */}
      <div>
        <div className="flex items-center gap-1 mb-1">
          <Layers className="w-3 h-3 text-zinc-400" />
          <span className="text-[10px] font-mono uppercase text-zinc-500">Palette</span>
          <button
            onClick={addPaletteColor}
            className="ml-auto text-[9px] text-[#06B6D4] hover:text-white"
            data-testid="tilemap-add-tile"
          >+ Tile</button>
        </div>
        <div className="flex flex-wrap gap-1">
          {palette.map(p => (
            <button
              key={p.id}
              onClick={() => { setBrush(p.id); setErasing(false); }}
              className={`w-6 h-6 rounded border ${brush === p.id && !erasing ? 'border-white ring-1 ring-white' : 'border-[#1E1E24]'}`}
              style={{ backgroundColor: p.color }}
              title={p.name}
              data-testid={`tilemap-brush-${p.id}`}
            />
          ))}
          <button
            onClick={() => setErasing(e => !e)}
            className={`w-6 h-6 rounded border flex items-center justify-center ${erasing ? 'bg-red-500/30 border-red-500' : 'border-[#1E1E24] bg-[#121214]'} text-white`}
            title="Eraser"
            data-testid="tilemap-eraser"
          >
            <Eraser className="w-3 h-3" />
          </button>
        </div>
      </div>

      {/* Grid */}
      <div
        className="grid overflow-auto max-h-[220px] border border-[#1E1E24] rounded bg-[#121214] p-1"
        style={{ gridTemplateColumns: `repeat(${cols}, minmax(0, 1fr))`, gap: '1px' }}
        data-testid="tilemap-grid"
      >
        {cells.map((row, r) => row.map((v, c) => {
          const pal = palette.find(p => p.id === v);
          return (
            <div
              key={`${r}-${c}`}
              onMouseDown={() => paint(r, c)}
              onMouseEnter={(e) => { if (e.buttons === 1) paint(r, c); }}
              className="aspect-square rounded-sm cursor-pointer hover:brightness-125"
              style={{ backgroundColor: pal?.color || '#1E1E24' }}
              data-testid={`tilemap-cell-${r}-${c}`}
            />
          );
        }))}
      </div>

      <div className="flex gap-1">
        <Button
          size="sm" onClick={save}
          className="flex-1 h-7 bg-[#06B6D4] text-black hover:bg-[#06B6D4]/90 text-[10px]"
          data-testid="tilemap-save"
        >
          <Save className="w-3 h-3 mr-1" /> Save Tilemap
        </Button>
        <Button
          size="sm" variant="ghost" onClick={clear}
          className="h-7 text-red-400 hover:bg-red-500/10 text-[10px]"
          data-testid="tilemap-clear"
        >
          Clear
        </Button>
      </div>
    </div>
  );
}
